/**
 * AI Chat hook — Phase 9D.2
 *
 * Manages chat messages with the neural-engine backend.
 * Context-aware: sends current page and entity info with each message.
 */

import { useState, useCallback, useRef } from 'react';
import { apiClient } from '../api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  suggestedActions?: AISuggestedAction[];
  sources?: AIChatSource[];
}

export interface AISuggestedAction {
  label: string;
  path?: string;
  type: 'navigate' | 'action' | 'info';
  description: string;
}

export interface AIChatSource {
  id: string;
  entityType: string;
  title: string;
}

export interface AIChatContext {
  currentPage: string;
  currentEntityType?: string;
  currentEntityId?: string;
  currentEntityName?: string;
}

interface ChatResponse {
  conversationId: string;
  message: {
    role: 'assistant';
    content: string;
    timestamp: string;
  };
  suggestedActions?: AISuggestedAction[];
  sources?: AIChatSource[];
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAIChat() {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const idCounterRef = useRef(0);

  const generateId = useCallback(() => {
    idCounterRef.current += 1;
    return `msg-${Date.now()}-${idCounterRef.current}`;
  }, []);

  const sendMessage = useCallback(
    async (content: string, context: AIChatContext) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      const userMessage: AIChatMessage = {
        id: generateId(),
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, userMessage]);
      setLoading(true);
      setError(null);

      try {
        const data = await apiClient.post<ChatResponse>('/neural/chat', {
          message: trimmed,
          conversationId: conversationIdRef.current,
          context: {
            currentPage: context.currentPage,
            selectedEntityId: context.currentEntityId,
            selectedEntityType: context.currentEntityType,
            selectedEntityName: context.currentEntityName,
          },
        });

        conversationIdRef.current = data.conversationId;

        const assistantMessage: AIChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: data.message.content,
          timestamp: data.message.timestamp,
          suggestedActions: data.suggestedActions,
          sources: data.sources,
        };

        setMessages(prev => [...prev, assistantMessage]);
      } catch {
        setError('AI chat unavailable');
        const fallbackMessage: AIChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: 'I\'m having trouble connecting right now. Please try again in a moment.',
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, fallbackMessage]);
      } finally {
        setLoading(false);
      }
    },
    [generateId],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    conversationIdRef.current = null;
  }, []);

  return { messages, loading, error, sendMessage, clearMessages };
}
